import { isPlatformBrowser } from '@angular/common';
import { afterNextRender, ChangeDetectionStrategy, Component, ElementRef, inject, PLATFORM_ID, viewChild } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { ErrorReportStore } from '@imagise/entities';
import { getRuntimeWindow } from '@imagise/shared';
import { ErrorReportFormComponent } from '../error-report-form/error-report-form.component';

/**
 * Full-page recovery screen shown after a fatal crash (route target for
 * `errorRoutePath`). Presents a friendly message, recovery actions, and — when
 * an incident is available — the embedded {@link ErrorReportFormComponent}.
 */
@Component({
  selector: 'lib-error-recovery-page',
  standalone: true,
  imports: [ErrorReportFormComponent, TranslateModule],
  templateUrl: './error-recovery-page.component.html',
  styleUrl: './error-recovery-page.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ErrorRecoveryPageComponent {
  readonly #store = inject(ErrorReportStore);
  readonly #router = inject(Router);
  readonly #platformId = inject(PLATFORM_ID);

  /** The active incident, if the user reached this page through a crash. */
  readonly incident = this.#store.incident;

  readonly heading = viewChild<ElementRef<HTMLElement>>('heading');

  constructor() {
    afterNextRender(() => this.heading()?.nativeElement.focus());
  }

  goHome(): void {
    this.#store.clear();
    void this.#router.navigateByUrl('/').catch(() => undefined);
  }

  reload(): void {
    if (isPlatformBrowser(this.#platformId)) {
      getRuntimeWindow().location.reload();
    }
  }
}
